import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  openModalId, resolveTarget, rowToRightClick, topOverlayRect, WalkthroughTarget,
} from "../utils/resolveTarget";
import { calloutPosition, CALLOUT_WIDTH } from "../utils/calloutPosition";
import { AISupportLang, dirFor, t } from "../i18n/aiSupport";

/**
 * Where a step happens, and how a person gets there. The same shape whether it
 * describes one step or the whole guide's starting point — the backend builds
 * both from the interface map.
 */
export interface WalkthroughPlace {
  entry: string;
  route: string;
  name?: string;
  /** Set when this happens inside a popup opened from that screen. */
  then_open?: string;
  /** That popup's name in a therapist's words, not its component id. */
  then_open_name?: string;
  /**
   * A real URL, not the `route` template. Captured when the guide was built:
   * the therapist's own address if they were already on the starting screen,
   * otherwise one filled in from the ids their browser sent. Absent when
   * neither was possible, and then there is nothing to offer.
   */
  url?: string;
}

export interface WalkthroughStep {
  screen?: string;
  modal?: string;
  instruction: string;
  target?: WalkthroughTarget;
  precondition?: string;
  /** Where this step happens — so a missed target can say where to go. */
  at?: WalkthroughPlace;
}

export interface Walkthrough {
  title: string;
  steps: WalkthroughStep[];
  /** Where the guide begins — the browser checks before drawing anything. */
  starts_at?: WalkthroughPlace;
}

interface Props {
  walkthrough: Walkthrough;
  lang: AISupportLang;
  onClose: () => void;
}

  /** Above modals (1000-1300) and the assistant panel (2100). */
const Z_INDEX = 2400;
const TEAL = "#3A7380";
const PAD = 6;

const KEYFRAMES = `
@keyframes aiSupportHalo {
  0%, 100% { box-shadow: 0 0 0 3px rgba(58,115,128,0.9), 0 0 0 9999px rgba(15,26,28,0.45) }
  50%      { box-shadow: 0 0 0 6px rgba(58,115,128,0.5), 0 0 0 9999px rgba(15,26,28,0.45) }
}
@media (prefers-reduced-motion: reduce) {
  .ai-support-spotlight { animation: none !important }
}`;

/**
 * Step-by-step guidance drawn over the real interface: the page dims, the actual
 * control is cut out and outlined, and a callout sits beside it.
 *
 * When a step's control cannot be found on screen the step degrades to plain
 * text rather than pointing at nothing — a walkthrough that silently highlights
 * the wrong thing is worse than one that admits it is lost.
 */
const INTERACTIVE = "button, a, input, select, textarea, summary, [role='button'], [role='tab'], [role='link'], [role='menuitem'], [role='option'], [role='checkbox'], [tabindex]";

/** Whether the highlighted element is a control a therapist actually presses. */
const isInteractive = (el: Element): boolean => {
  try {
    return el.matches(INTERACTIVE);
  } catch {
    return false;
  }
};


/** Trailing slashes are the only difference tolerated between two paths. */
const samePath = (a: string, b: string) =>
  a.replace(/\/+$/, "") === b.replace(/\/+$/, "");

/**
 * Is the therapist standing where this step happens?
 *
 * Whole-path, never a prefix: "/patients" starts with "/patient", so prefix
 * matching counted the patients LIST as one patient's page and then highlighted
 * nothing. When the stored URL names a record id, that has to match too -- a
 * guide built around one patient, replayed while looking at another, would
 * point at the right controls against the wrong record.
 */
export const isAtPlace = (place?: { route: string; url?: string }): boolean => {
  if (!place) return true;
  if (typeof window === "undefined") return true;
  const target = place.url || place.route;
  const [path, query] = target.split("?");
  if (!samePath(window.location.pathname, path)) return false;
  const wantedId = new URLSearchParams(query || "").get("id");
  if (!wantedId || wantedId.includes("{")) return true;
  return new URLSearchParams(window.location.search).get("id") === wantedId;
};

/**
 * Which step to open on.
 *
 * A guide recorded on the calendar opens with the steps that walk to the
 * patient page. Replayed by someone already on that patient page, those steps
 * send them away and back again to arrive where they started. So begin at the
 * first step that happens where they already are.
 *
 * Only ever moves forward past steps for screens they are NOT on, and the
 * counter shows it ("3/5") with Back one click away, so a skip is visible and
 * reversible rather than silent.
 */
const openingStep = (walkthrough: Walkthrough): number => {
  // A popup already on screen. The steps before the first one that happens
  // inside it exist only to open it, and they have plainly already been done --
  // the page says so. Telling somebody to open a window they are looking at is
  // the clearest possible sign the guide is not paying attention.
  const openModal = openModalId();
  if (openModal) {
    const inModal = walkthrough.steps.findIndex((s) => s.modal === openModal);
    // Only when something ahead happens in that popup, and only forwards.
    if (inModal > 0) return inModal;
  }

  // Otherwise by screen. Walk forward only while each step is one we can
  // positively place SOMEWHERE ELSE. A step we cannot place stops the scan
  // rather than being jumped over: not knowing where it happens is not a
  // reason to decide the therapist has already done it.
  let i = 0;
  while (i < walkthrough.steps.length) {
    const at = walkthrough.steps[i].at;
    if (!at || isAtPlace(at)) break;
    i += 1;
  }
  return i < walkthrough.steps.length ? i : 0;
};

const AISupportWalkthrough: React.FC<Props> = ({ walkthrough, lang, onClose }) => {
  const [index, setIndex] = useState(() => openingStep(walkthrough));
  const [rect, setRect] = useState<DOMRect | null>(null);
  // Last target reported as unmatched, so the warning below fires once rather
  // than on every one of the poll's ticks.
  const missRef = useRef<string | null>(null);
  const [overlayRect, setOverlayRect] = useState<DOMRect | null>(null);
  const rafRef = useRef<number | null>(null);
  // The element the current step points at, so a click on it can advance us.
  const targetRef = useRef<HTMLElement | null>(null);
  const advanceRef = useRef<() => void>(() => {});
  // Bumped on navigation. Its only job is to re-run the render, which re-reads
  // the address to decide whether they are where the step happens.
  const [, setPlaceTick] = useState(0);

  const step = walkthrough.steps[index];
  const isLast = index === walkthrough.steps.length - 1;
  const dir = dirFor(lang);

  const locate = useCallback(() => {
    setOverlayRect(topOverlayRect());
    if (!step?.target?.value) {
      setRect(null);
      targetRef.current = null;
      return;
    }
    const el = resolveTarget(step.target) || rowToRightClick(step.target);
    if (!el) {
      // Say so once per target rather than failing mutely: the step still
      // renders as text, and without this there is nothing to explain why the
      // highlight is missing.
      if (missRef.current !== step.target.value) {
        missRef.current = step.target.value;
        console.warn(
          "[AISupport] walkthrough: no element matched target",
          step.target,
          "-- the step will show as text only."
        );
      }
      setRect(null);
      targetRef.current = null;
      return;
    }
    missRef.current = null;
    targetRef.current = el;
    const box = el.getBoundingClientRect();
    // Bring an offscreen control into view, but still draw on the box we have.
    // Returning here instead left the highlight undrawn until a scroll event
    // arrived -- and when the scroll could not satisfy the condition (a control
    // taller than the viewport, a container already at its end, or the panel
    // itself covering it) that was never, so the guide silently pointed at
    // nothing. The listener refines the box as the scroll lands.
    if (box.top < 0 || box.bottom > window.innerHeight) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    setRect(box);
  }, [step]);

  // Re-locate on scroll, resize, and DOM changes: opening the modal a step asks
  // for changes the layout, and the highlight has to follow.
  useEffect(() => {
    locate();
    const schedule = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(locate);
    };
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    const poll = setInterval(schedule, 600);
    return () => {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      clearInterval(poll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [locate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Where the therapist is standing decides whether this draws the guide or the
  // offer to go to its starting screen, and the app changes screens without a
  // reload -- so the overlay has to hear the navigation. Without this it waited
  // on the locate poll, and "Take me there" left them looking at the same
  // "this starts on another screen" card for another half second.
  useEffect(() => {
    const onNavigate = () => setPlaceTick((n) => n + 1);
    window.addEventListener("popstate", onNavigate);
    return () => window.removeEventListener("popstate", onNavigate);
  }, []);

  /**
   * Doing the step is the natural way to finish it, so a click on the
   * highlighted control moves on by itself — pressing "Next" as well would be
   * asking the therapist to confirm what they just did.
   *
   * Listens in the bubble phase and waits a moment, so the app's own handler
   * runs first and any modal the click opens is on screen before the next step
   * tries to find its target.
   */
  useEffect(() => {
    const onDocumentClick = (e: MouseEvent) => {
      const el = targetRef.current;
      if (!el || !(e.target instanceof Node)) return;
      // Only a click on the highlighted control counts. When the step points at
      // a container rather than a control, `contains` is true for every click
      // inside it -- which advanced the guide, and closed it on the last step,
      // whenever the therapist clicked anywhere in the popup.
      if (!isInteractive(el)) return;
      if (el === e.target || el.contains(e.target)) {
        window.setTimeout(() => advanceRef.current(), 400);
      }
    };
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, []);

  if (!step) return null;

  // A guide that begins on a screen the therapist is not looking at highlights
  // nothing and reads as broken. Say where to go instead of pointing at a page
  // that is not there.


  // The place this step happens: its own, falling back to where the guide began.
  const startsAt = step.at || walkthrough.starts_at;
  const onStartingScreen = isAtPlace(startsAt);

  if (!onStartingScreen && startsAt) {
    return (
      <div
        dir={dir}
        data-ai-walkthrough=""
        style={{
          position: "fixed",
          top: Math.max(24, window.innerHeight / 2 - 110),
          left: Math.min(Math.max(12, window.innerWidth / 2 - CALLOUT_WIDTH / 2),
                         window.innerWidth - CALLOUT_WIDTH - 12),
          width: CALLOUT_WIDTH, zIndex: Z_INDEX + 1, background: "#fff",
          borderRadius: 8, border: "1px solid #a9bcb9",
          boxShadow: "0 10px 34px rgba(15,26,28,0.35)",
          padding: "14px 16px", fontSize: 14, lineHeight: 1.6,
        }}
      >
        <div dir="auto" style={{
          fontSize: 13, fontWeight: 600, color: "#24484f", marginBottom: 8,
          paddingBottom: 7, borderBottom: "1px solid #e4ebea", lineHeight: 1.4,
        }}>
          {t(lang, "walkthroughStartHere")}
        </div>
        <div style={{ marginBottom: 12 }}>
          {t(lang, "walkthroughOpenFirst")}{" "}
          <b>{startsAt.name || startsAt.entry}</b>
          {startsAt.then_open
            ? ` (${startsAt.then_open_name || startsAt.then_open})`
            : ""}
        </div>
        <div style={{ display: "flex", gap: 7 }}>
          {/* Only offer this where it would actually land. `url` is the real
              address captured when the guide was built; the bare route is a
              usable fallback only when it carries no placeholder. */}
          {(startsAt.url || !startsAt.route.includes("{")) && (
          <button
            type="button"
            data-ai-id="walkthrough-go-to-start"
            onClick={() => {
              // In place, via the app's own routing. A location assignment
              // reloads the page, which tears down the widget and the guide --
              // so the old button destroyed the very walkthrough it was
              // resuming, and dropped the query string on the way.
              const to = startsAt.url || startsAt.route;
              window.history.pushState({}, "", to);
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
            style={{
              flex: 1, padding: "5px 12px", borderRadius: 4, border: "none",
              background: TEAL, color: "#fff", fontSize: 12.5, cursor: "pointer",
            }}
          >
            {t(lang, "walkthroughTakeMeThere")}
          </button>
          )}
          <button
            type="button"
            data-ai-id="walkthrough-close"
            onClick={onClose}
            style={{
              padding: "5px 12px", borderRadius: 4, border: "1px solid #b9c6c4",
              background: "#fff", color: "#4a5a5c", fontSize: 12.5, cursor: "pointer",
            }}
          >
            {t(lang, "close")}
          </button>
        </div>
      </div>
    );
  }

  const advance = () => (isLast ? onClose() : setIndex((i) => i + 1));
  // Kept in a ref so the document listener always calls the current one without
  // being torn down and rebound on every step.
  advanceRef.current = advance;

  const { top: calloutTop, left: calloutLeft } = calloutPosition(
    rect,
    overlayRect,
    { width: window.innerWidth, height: window.innerHeight }
  );

  return (
    <>
      <style>{KEYFRAMES}</style>

      {/* The scrim. When there is no highlight it covers everything; when there
          is, the cut-out is drawn by the spotlight's huge outer box-shadow. */}
      {!rect && (
        <div
          aria-hidden
          data-ai-walkthrough=""
          style={{
            position: "fixed", inset: 0, background: "rgba(15,26,28,0.35)",
            zIndex: Z_INDEX,
            // Dim only. The therapist must still be able to click the app --
            // and a stray click must never dismiss the guide.
            pointerEvents: "none",
          }}
        />
      )}

      {rect && (
        <div
          className="ai-support-spotlight"
          data-ai-walkthrough=""
          style={{
            position: "fixed",
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 6,
            zIndex: Z_INDEX,
            pointerEvents: "none",
            animation: "aiSupportHalo 1.8s ease-in-out infinite",
          }}
        />
      )}

      <div
        dir={dir}
        data-ai-walkthrough=""
        style={{
          position: "fixed", top: calloutTop, left: calloutLeft, width: CALLOUT_WIDTH,
          zIndex: Z_INDEX + 1, background: "#fff", borderRadius: 8,
          border: "1px solid #a9bcb9", boxShadow: "0 10px 34px rgba(15,26,28,0.35)",
          padding: "13px 15px", fontSize: 14, lineHeight: 1.6,
        }}
      >
        {/* The title is the therapist's own words back at them, so it is read,
            not decoration: it wraps at full size instead of being squeezed onto
            one clipped monospace line, and `dir="auto"` keeps a Hebrew title
            from being reordered around the counter. The counter is a separate
            element on the far side, always left-to-right. */}
        <div style={{
          display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8,
          paddingBottom: 7, borderBottom: "1px solid #e4ebea",
        }}>
          <div dir="auto" style={{
            flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600,
            color: "#24484f", lineHeight: 1.4, overflowWrap: "anywhere",
          }}>
            {walkthrough.title}
          </div>
          <div style={{
            flexShrink: 0, fontSize: 11.5, color: TEAL, direction: "ltr",
            fontVariantNumeric: "tabular-nums",
          }}>
            {index + 1}/{walkthrough.steps.length}
          </div>
        </div>

        <div dir="auto">{step.instruction}</div>

        {step.precondition && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#8A5A12" }} dir="auto">
            {step.precondition}
          </div>
        )}

        {step.target?.value && !rect && (() => {
          // A data-ai-id is an internal name. Quoting it at a therapist tells
          // them nothing and looks like a fault in the product, so only a label
          // -- something they could actually read on screen -- is named.
          const named = step.target.by === "testid" ? null : step.target.value;
          // The popup the control lives in, when it lives in one: "open the
          // patient page first" is unhelpful to somebody already on it whose
          // real problem is that the meetings window is closed.
          // A right-click option is the first thing to rule out: it exists, it
          // is simply not rendered until the menu is open. Telling somebody to
          // open the screen they are standing on -- which is what the branch
          // below did on the ad-hoc screen -- reads as the guide being broken.
          const inMenu = step.target?.in_right_click_menu;
          // "Open the patient page first" is only true if they are not already
          // on it. The place is checked rather than assumed, or a therapist in
          // exactly the right spot is sent to where they already are.
          const elsewhere = !isAtPlace(step.at);
          const where =
            step.at?.then_open_name || (elsewhere ? step.at?.name : undefined);
          const text =
            inMenu
              ? lang === "he"
                ? named
                  ? `״${named}״ נמצא בתפריט של השורה — לחצו קליק ימני על השורה כדי לפתוח אותו.`
                  : "השלב הזה נמצא בתפריט של השורה — לחצו קליק ימני על השורה."
                : named
                ? `“${named}” is in the row's right-click menu — right-click the row to open it.`
                : "This step is in the row's right-click menu — right-click the row."
              : where && named
              ? lang === "he"
                ? `״${named}״ נמצא ב${where} — יש לפתוח אותו קודם.`
                : `“${named}” is in ${where} — open that first.`
              : where
              ? lang === "he"
                ? `השלב הזה מתבצע ב${where} — יש לפתוח אותו קודם.`
                : `This step happens in ${where} — open that first.`
              : named
              ? lang === "he"
                ? `לא מצאתי את ״${named}״ במסך הנוכחי.`
                : `I can't find “${named}” on this screen.`
              : lang === "he"
                ? "השלב הזה שייך למסך אחר."
                : "This step belongs to another screen.";
          return (
            <div style={{ marginTop: 8, fontSize: 12, color: "#8E2F41" }} dir="auto">
              {text}
            </div>
          );
        })()}

        <div style={{ display: "flex", gap: 6, marginTop: 11 }}>
          <button
            data-ai-id="walkthrough-next"
            type="button"
            onClick={advance}
            style={{
              flex: 1, padding: "5px 12px", borderRadius: 4, border: "none",
              background: TEAL, color: "#fff", fontSize: 12.5, cursor: "pointer",
            }}
          >
            {isLast ? (lang === "he" ? "סיום" : "Done") : lang === "he" ? "הבא" : "Next"}
          </button>
          {index > 0 && (
            <button
              type="button"
              onClick={() => setIndex((i) => i - 1)}
              style={{
                padding: "5px 12px", borderRadius: 4, border: "1px solid #b9c6c4",
                background: "#fff", color: "#4a5a5c", fontSize: 12.5, cursor: "pointer",
              }}
            >
              {lang === "he" ? "הקודם" : "Back"}
            </button>
          )}
          <button
            data-ai-id="walkthrough-close"
            type="button"
            onClick={onClose}
            style={{
              padding: "5px 12px", borderRadius: 4, border: "1px solid #b9c6c4",
              background: "#fff", color: "#4a5a5c", fontSize: 12.5, cursor: "pointer",
            }}
          >
            {t(lang, "close")}
          </button>
        </div>
      </div>
    </>
  );
};

export default AISupportWalkthrough;
